import { useState, useCallback } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useOnline } from '@/hooks/useOnline';

/* ==========================================================================
   OnlineIndicator — subtle status pill showing connectivity & sync state.

   Positioned in the header. Tap to force sync or see pending count.
   ========================================================================== */

export function OnlineIndicator() {
  const { isOnline, syncStatus, pendingCount, forceSync } = useOnline();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleTap = useCallback(async () => {
    if (!isOnline || isSyncing) return;
    setIsSyncing(true);
    try {
      await forceSync();
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, forceSync]);

  // Determine visual state
  const isOffline = !isOnline || syncStatus === 'offline';
  const isSyncActive = syncStatus === 'syncing' || isSyncing;
  const hasError = syncStatus === 'error';
  const isIdle = !isOffline && !isSyncActive && !hasError;

  return (
    <button
      type="button"
      onClick={handleTap}
      disabled={isOffline || isSyncActive}
      className={cn(
        'inline-flex items-center gap-1.5',
        'px-2.5 py-1 rounded-[var(--radius-full)]',
        'text-[11px] font-medium leading-none',
        'transition-all duration-[var(--duration-fast)]',
        'select-none cursor-pointer',
        'disabled:cursor-default',

        // Background & text by state
        isOffline && 'bg-amber-50 text-amber-700 border border-amber-200',
        isSyncActive && 'bg-blue-50 text-blue-600 border border-blue-200',
        hasError && 'bg-red-50 text-red-600 border border-red-200',
        isIdle && pendingCount > 0 && 'bg-surface-sunken text-text-secondary border border-border-default',
        isIdle && pendingCount === 0 && 'bg-status-positive-light text-status-positive border border-transparent',
      )}
      aria-label={
        isOffline
          ? 'Offline'
          : isSyncActive
            ? 'Sincronizzazione in corso'
            : hasError
              ? 'Errore di sincronizzazione — tocca per riprovare'
              : pendingCount > 0
                ? `${pendingCount} elementi in attesa — tocca per sincronizzare`
                : 'Online — tutto sincronizzato'
      }
    >
      {/* Status dot + icon */}
      {isOffline && (
        <>
          <WifiOff className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>Offline</span>
          {pendingCount > 0 && (
            <span
              className={cn(
                'inline-flex items-center justify-center',
                'ml-0.5 min-w-[16px] h-4 px-1',
                'rounded-[var(--radius-full)]',
                'bg-amber-200 text-amber-800',
                'text-[10px] font-bold leading-none',
              )}
            >
              {pendingCount}
            </span>
          )}
        </>
      )}

      {isSyncActive && (
        <>
          <RefreshCw className="h-3 w-3 shrink-0 animate-spin" strokeWidth={2.5} />
          <span>Sincronizzazione...</span>
        </>
      )}

      {hasError && (
        <>
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>Errore sync</span>
        </>
      )}

      {isIdle && pendingCount > 0 && (
        <>
          <RefreshCw className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>{pendingCount} in coda</span>
        </>
      )}

      {isIdle && pendingCount === 0 && (
        <>
          <Wifi className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>Online</span>
        </>
      )}
    </button>
  );
}
